import React, { useEffect, useRef } from 'react';
import { ImageGallerySkeleton, ImageGeneratingTile } from './ImageGeneratingTile';
import { ImageCard } from './ImageCard';
import { useI18n } from '../../hooks/useI18n';

export const ImageGallery = ({
  items = [],
  loading = false,
  loadingMore = false,
  generating = false,
  generatingPrompt = '',
  hasMore = false,
  onLoadMore,
  onView,
  onDownload,
  onDelete,
  onCopyLink
}) => {
  const { t } = useI18n();
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || loading || loadingMore || generating) {
      return undefined;
    }
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onLoadMore?.();
        }
      },
      { rootMargin: '240px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [generating, hasMore, loading, loadingMore, onLoadMore]);

  if (loading && items.length === 0 && !generating) {
    return <ImageGallerySkeleton />;
  }

  return (
    <section aria-label={t('imageGalleryHeading')}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {generating ? <ImageGeneratingTile prompt={generatingPrompt} /> : null}
        {items.map((item) => (
          <ImageCard
            key={`${item.messageId}-${item.url}`}
            item={item}
            onView={onView}
            onDownload={onDownload}
            onDelete={onDelete}
            onCopyLink={onCopyLink}
          />
        ))}
      </div>
      {hasMore ? <div ref={sentinelRef} className="h-8" aria-hidden="true" /> : null}
      {loadingMore ? (
        <div className="mt-3">
          <ImageGallerySkeleton count={4} />
        </div>
      ) : null}
    </section>
  );
};
